import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { generateAccount } from '@/test/data-generators';
import { db, persistDb } from '@/test/mocks/db';
import {
  authenticate,
  hash,
  networkDelay,
  omit,
  setMockUserAuthToken,
} from '@/test/mocks/utils';

import {
  UserRegistrationResponse,
  UserRegistrationDetailsResponse,
  AuthUser,
} from '../types';

export type RegisterDetails = {
  email: string;
  password: string;
  source?: string;
};

export const register = (
  data: RegisterDetails
): Promise<UserRegistrationResponse.Root> => {
  return axios.post('/user/v1/register', data);
};

export const getRegistrationDetails = (
  token: string | null
): Promise<UserRegistrationDetailsResponse.Root> => {
  return axios.post('/user/v1/registration-details', { token });
};

export const registerMockHandler = http.post(
  `${env.API_URL}/user/v1/register`,
  async ({ request }): Promise<HttpResponse<UserRegistrationResponse.Root>> => {
    await networkDelay();

    const userObject = (await request.json()) as RegisterDetails;

    const existingUser = db.user.update({
      where: {
        email: {
          equals: userObject.email,
        },
      },
      data: { ...userObject, password: hash(userObject.password) },
    });

    if (!existingUser) {
      throw new Error('User not found');
    }

    persistDb('user');

    const result = authenticate({
      email: userObject.email,
      password: userObject.password,
    });

    setMockUserAuthToken(result.cookie);

    // Transform the user to match AuthUser type
    const authUser: AuthUser = {
      id: result.user.id,
      email_hash: userObject.email, // Using email as email_hash for mock
      base_lead_id: result.user.base_lead_id,
      consent_granted: true, // Default to true for mock
      practice_id: result.user.practice_id,
      selected_variant_id: null,
    };

    return HttpResponse.json({
      status: 'Success',
      message: 'Registration successful',
      user: authUser,
    });
  }
);

const handleTokenRegistration = (
  token: string
): HttpResponse<UserRegistrationDetailsResponse.Root> | string => {
  const users = db.user.findMany({
    where: {
      id: {
        equals: token,
      },
    },
  });

  const user = users.length > 0 ? users[0] : null;
  if (!user) {
    return HttpResponse.json(
      {
        message: 'User not found',
        registration_email: '',
      },
      { status: 400 }
    );
  }

  if (user.password) {
    return HttpResponse.json(
      {
        message: 'User already registered',
        registration_email: user.email,
      },
      { status: 400 }
    );
  }

  return user.email;
};

const createMockAccounts = () => {
  const accounts = generateAccount(true, 2);
  let primaryEmail = '';

  for (const account of accounts) {
    // Create courses first if they don't exist
    if (account.enrolled_courses) {
      for (const course of account.enrolled_courses) {
        const existingCourse = db.course.findFirst({
          where: { course_id: { equals: course.course_id } },
        });
        if (!existingCourse) {
          db.course.create(course);
        }
      }
    }

    // Create user without enrolled_courses to avoid relationship issues
    const userData = omit(account, ['enrolled_courses']);
    db.user.create(userData);

    primaryEmail = account?.primary_contact ? account?.email : primaryEmail;
  }
  persistDb('user');
  return primaryEmail;
};

export const registrationDetailsMockHandler = http.post(
  `${env.API_URL}/user/v1/registration-details`,
  async ({
    request,
  }): Promise<HttpResponse<UserRegistrationDetailsResponse.Root>> => {
    await networkDelay();

    const body = (await request.json()) as { token: string | null };
    const { token } = body;

    if (token) {
      const result = handleTokenRegistration(token);
      if (result instanceof HttpResponse) {
        return result;
      }
      return HttpResponse.json({
        status: 'Success',
        message: 'User found',
        registration_email: result,
      });
    }

    const primaryEmail = createMockAccounts();
    return HttpResponse.json({
      status: 'Success',
      message: 'User found',
      registration_email: primaryEmail,
    });
  }
);
